import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { DollarSign, TrendingUp, CreditCard, AlertTriangle } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'

export default function FinancialPage() {
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month')

  const { data: revenueData, isLoading } = useQuery(
    ['revenue-metrics', period],
    () => adminApi.getRevenueMetrics(period)
  )

  const { data: transactionsData } = useQuery(
    'payment-transactions',
    () => adminApi.getPaymentTransactions(1, 20)
  )

  const revenue = revenueData?.data
  const transactions = transactionsData?.data || []

  if (isLoading) {
    return <div className="animate-pulse">Loading financial data...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Financial Overview
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Revenue metrics, payment transactions, and financial analytics
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="input"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
          </select>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Daily Revenue</dt>
                <dd className="stat-value">${revenue?.daily_revenue?.toLocaleString() || '0'}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Monthly Revenue</dt>
                <dd className="stat-value">${revenue?.monthly_revenue?.toLocaleString() || '0'}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Credit Sales</dt>
                <dd className="stat-value">{revenue?.credit_sales?.length || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Failed Payments</dt>
                <dd className="stat-value">{revenue?.payment_failures?.length || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Revenue Trends</h3>
        </div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={[]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#10b981" fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Recent Transactions</h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Credits</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction: any) => (
                  <tr key={transaction.id}>
                    <td className="font-mono text-sm">{transaction.id}</td>
                    <td>{transaction.user_email}</td>
                    <td>${transaction.amount}</td>
                    <td>{transaction.credits}</td>
                    <td>
                      <span className={`badge ${
                        transaction.status === 'completed' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td>{new Date(transaction.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}